export interface ExportColumn<T> {
  header: string;
  value: (row: T) => string;
}

function toCsvCell(value: string): string {
  const clean = (value ?? "").replace(/"/g, '""');
  return `"${clean}"`;
}

export function buildCsv<T>(columns: ExportColumn<T>[], rows: T[]): string {
  const head = columns.map((col) => toCsvCell(col.header)).join(",");
  const body = rows.map((row) => columns.map((col) => toCsvCell(col.value(row))).join(",")).join("\n");
  return `${head}\n${body}`;
}

function triggerDownload(content: string, filename: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function exportToCsv<T>(filename: string, columns: ExportColumn<T>[], rows: T[]): void {
  const csv = buildCsv(columns, rows);
  triggerDownload(`\uFEFF${csv}`, filename.endsWith(".csv") ? filename : `${filename}.csv`, "text/csv;charset=utf-8;");
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export interface PdfOptions<T> {
  title: string;
  subtitle?: string;
  columns: ExportColumn<T>[];
  rows: T[];
}

/**
 * Opens a clean, print-ready document in a new window and triggers the print
 * dialog so the user can "Save as PDF". No external library required.
 */
export function exportToPdf<T>({ title, subtitle, columns, rows }: PdfOptions<T>): void {
  const win = window.open("", "_blank", "width=1100,height=800");
  if (!win) return;

  const head = columns.map((col) => `<th>${escapeHtml(col.header)}</th>`).join("");
  const body = rows
    .map(
      (row) =>
        `<tr>${columns.map((col) => `<td>${escapeHtml(col.value(row))}</td>`).join("")}</tr>`,
    )
    .join("");
  const generated = new Date().toLocaleString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  win.document.write(`<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)} — Rythugaru Agri Tourism</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Georgia, "Times New Roman", serif; color: #1c2b22; margin: 0; padding: 40px; }
  .brand { font-size: 12px; letter-spacing: 0.24em; text-transform: uppercase; color: #5c7a5c; font-family: Arial, sans-serif; }
  h1 { font-size: 26px; margin: 8px 0 4px; }
  .sub { color: #4a5b50; font-size: 14px; margin: 0 0 4px; font-family: Arial, sans-serif; }
  .meta { color: #7a8a7f; font-size: 12px; margin: 0 0 22px; font-family: Arial, sans-serif; }
  table { width: 100%; border-collapse: collapse; font-family: Arial, sans-serif; font-size: 12px; }
  th { text-align: left; background: #eef3ec; color: #33463a; padding: 9px 10px; border-bottom: 2px solid #c6d6c6; text-transform: uppercase; letter-spacing: 0.06em; font-size: 10px; }
  td { padding: 9px 10px; border-bottom: 1px solid #e2e8e0; vertical-align: top; }
  tr:nth-child(even) td { background: #fafbf9; }
  footer { margin-top: 26px; font-size: 10px; color: #8a978e; font-family: Arial, sans-serif; }
  @media print { body { padding: 18px; } }
</style>
</head>
<body>
  <div class="brand">Rythugaru Agri Tourism</div>
  <h1>${escapeHtml(title)}</h1>
  ${subtitle ? `<p class="sub">${escapeHtml(subtitle)}</p>` : ""}
  <p class="meta">Generated ${escapeHtml(generated)} · ${rows.length} record${rows.length === 1 ? "" : "s"}</p>
  <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
  <footer>Confidential — for internal Rythugaru operations use only.</footer>
</body>
</html>`);

  win.document.close();
  win.focus();
  window.setTimeout(() => win.print(), 350);
}